import {
  Box, Button, FormLabel, IconButton, Link, Modal, TextField,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useForm } from 'react-hook-form';
import { useEffect, useState } from 'react';

interface TwoFactorAuthModalProps {
  show: boolean;
  onVerify: (code: string) => void;
  onClose: () => void;
  onResendCode?: () => void | Promise<void>;
  loading?: boolean;
  errorMessage?: string;
  isAccountLocked?: boolean;
}

interface TwoFactorFormData {
  code: string;
}

function TwoFactorAuthModal({
  show,
  onVerify,
  onClose,
  onResendCode,
  loading = false,
  errorMessage = '',
  isAccountLocked = false,
}: TwoFactorAuthModalProps) {
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setError,
    clearErrors,
  } = useForm<TwoFactorFormData>({
    defaultValues: {
      code: '',
    },
  });
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);

  useEffect(() => {
    if (errorMessage) {
      setError('code', {
        type: 'server',
        message: errorMessage,
      });
    } else {
      clearErrors('code');
    }
  }, [errorMessage, setError, clearErrors]);

  const onSubmit = (data: TwoFactorFormData) => {
    onVerify(data.code);
  };

  const handleClose = () => {
    reset();
    setResendSuccess(false);
    onClose();
  };

  const handleResendCode = async () => {
    if (!onResendCode) return;

    setResendLoading(true);
    setResendSuccess(false);
    clearErrors('code');
    setError('code', { type: 'manual', message: '' });

    try {
      await onResendCode();
      setResendSuccess(true);
      clearErrors('code');
      setTimeout(() => setResendSuccess(false), 3000);
    } catch (error) {
      // Error handling is done by parent component through errorMessage prop
    } finally {
      setResendLoading(false);
    }
  };

  const modalStyle = {
    position: 'absolute' as const,
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '90%',
    maxWidth: 500,
    bgcolor: 'background.paper',
    border: '1px solid #e0e0e0',
    borderRadius: 2,
    boxShadow: 24,
    p: 4,
  };

  return (
    <Modal
      open={show}
      onClose={(event, reason) => {
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
          return;
        }
        if (!loading) {
          handleClose();
        }
      }}
      disableEscapeKeyDown
    >
      <Box sx={modalStyle}>
        <Box sx={{ position: 'relative', mb: 2 }}>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            disabled={loading && !isAccountLocked}
            sx={{
              position: 'absolute',
              right: 0,
              top: 0,
              color: 'text.secondary',
              '&:hover': {
                bgcolor: 'action.hover',
                color: 'text.primary',
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {isAccountLocked ? (
          <Box sx={{ textAlign: 'center' }}>
            <Box sx={{ mb: 2 }}>
              <img src="/img/logo.png" alt="ビルドセルフ" style={{ maxWidth: '200px', height: 'auto' }} />
            </Box>
            <FormLabel
              sx={{
                fontSize: '1.2rem',
                fontWeight: 'bold',
                display: 'block',
                mb: 2,
              }}
            >
              アカウントロック
            </FormLabel>
            <FormLabel
              sx={{
                fontSize: '0.9rem',
                color: 'text.secondary',
                display: 'block',
                mb: 3,
              }}
            >
              ログイン試行回数の上限に達したため、アカウントはロックされました。
            </FormLabel>
            <Button
              variant="contained"
              fullWidth
              onClick={handleClose}
              sx={{
                py: 1.5,
                fontSize: '1rem',
                backgroundColor: '#1976d2',
                '&:hover': {
                  backgroundColor: '#1565c0',
                },
              }}
            >
              閉じる
            </Button>
          </Box>
        ) : (
          <>
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <Box sx={{ mb: 2 }}>
                <img src="/img/logo.png" alt="ビルドセルフ" style={{ maxWidth: '200px', height: 'auto' }} />
              </Box>
              <FormLabel
                sx={{
                  fontSize: '1.2rem',
                  fontWeight: 'bold',
                  display: 'block',
                  mb: 2,
                }}
              >
                2段階認証
              </FormLabel>
              <FormLabel sx={{ fontSize: '0.9rem', color: 'text.secondary', display: 'block' }}>
                ご登録メールアドレスに届いた認証コードを入力してください。
              </FormLabel>
            </Box>

            <Box component="form" onSubmit={handleSubmit(onSubmit)}>
              <Box
                sx={{
                  mb: 3,
                  p: 2,
                  bgcolor: 'background.default',
                  borderRadius: 1,
                }}
              >
                <TextField
                  {...register('code', {
                    required: '認証コードを入力してください',
                    onChange: () => {
                      if (errors.code?.type === 'server') {
                        clearErrors('code');
                      }
                    },
                  })}
                  label="認証コード"
                  placeholder="認証コードを入力してください"
                  variant="outlined"
                  fullWidth
                  error={!!errors.code}
                  helperText={errors.code?.message || ' '}
                  disabled={loading}
                  autoFocus
                />
              </Box>

              <Button
                type="submit"
                variant="contained"
                fullWidth
                disabled={loading || resendLoading}
                sx={{
                  py: 1.5,
                  fontSize: '1rem',
                  backgroundColor: '#1976d2',
                  '&:hover': {
                    backgroundColor: '#1565c0',
                  },
                  mb: 2,
                }}
              >
                {loading ? '確認中...' : '送信する'}
              </Button>

              {onResendCode && (
                <Box sx={{ textAlign: 'center', mt: 2 }}>
                  {resendSuccess ? (
                    <FormLabel sx={{ fontSize: '0.9rem', color: 'success.main', display: 'block' }}>
                      認証コードを再送信しました
                    </FormLabel>
                  ) : (
                    <Link
                      component="button"
                      type="button"
                      onClick={handleResendCode}
                      disabled={loading || resendLoading}
                      sx={{
                        fontSize: '0.9rem',
                        cursor: (loading || resendLoading) ? 'not-allowed' : 'pointer',
                        textDecoration: 'none',
                        '&:hover': {
                          textDecoration: 'underline',
                        },
                      }}
                    >
                      {resendLoading ? '送信中...' : '認証コードを再送信する'}
                    </Link>
                  )}
                </Box>
              )}
            </Box>
          </>
        )}
      </Box>
    </Modal>
  );
}

export default TwoFactorAuthModal;
